import { ThemeToggle } from '@/components/ThemeToggle';
import { Sparkles, Scissors, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface AuthSplitLayoutProps {
  heroTitle: string;
  heroHighlight: string;
  heroBenefits: string[];
  mobileSubtitle?: string;
  children: ReactNode;
}

export function AuthSplitLayout({
  heroTitle,
  heroHighlight,
  heroBenefits,
  mobileSubtitle = 'Seus agendamentos em um só lugar',
  children,
}: AuthSplitLayoutProps) {
  return (
    <div className="flex min-h-svh flex-col lg:flex-row">
      {/* Hero left panel – desktop only */}
      <div className="bg-primary relative hidden overflow-hidden lg:flex lg:w-1/2 xl:w-3/5">
        <div className="absolute inset-0 overflow-hidden">
          <div className="bg-accent/20 absolute -top-20 -left-20 h-96 w-96 rounded-full blur-3xl" />
          <div className="bg-secondary/20 absolute right-20 bottom-20 h-80 w-80 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/3 h-64 w-64 rounded-full bg-white/10 blur-2xl" />
        </div>

        <Link
          to="/"
          className="absolute top-6 left-6 z-20 flex min-h-11 items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Link>

        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-20">
          <div className="mb-12 flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
              <Scissors className="h-7 w-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-white">Vira Tempo</span>
          </div>

          <h1 className="mb-6 text-4xl leading-tight font-bold text-white xl:text-5xl">
            {heroTitle}
            <span className="text-accent block">{heroHighlight}</span>
          </h1>

          <ul className="space-y-4 text-white/90">
            {heroBenefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-3">
                <Sparkles className="text-accent h-5 w-5" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Form right panel */}
      <div className="bg-background relative flex min-w-0 flex-1 items-start justify-center px-4 pt-20 pb-8 sm:items-center sm:p-8 sm:pt-20 lg:p-12">
        <Link
          to="/"
          className="bg-muted text-foreground hover:bg-muted/80 absolute top-4 left-4 flex min-h-11 items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors lg:hidden"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Link>

        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>

        <div className="animate-fade-in-up w-full max-w-md">
          {/* Mobile-only branding */}
          <div className="mb-6 flex flex-col items-center lg:hidden">
            <div className="bg-primary mb-3 flex h-12 w-12 items-center justify-center rounded-2xl shadow-lg">
              <Scissors className="text-primary-foreground h-6 w-6" />
            </div>
            <h1 className="text-2xl font-bold">
              <span className="text-primary">Vira</span>
              <span className="text-foreground">Tempo</span>
            </h1>
            <p className="text-muted-foreground mt-1">{mobileSubtitle}</p>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
